import { defineRule, configure } from 'vee-validate';
import { required, email, min, regex, max, numeric, min_value, max_value, ext, length, between } from '@vee-validate/rules';
import {
  getReadableDate,
  getReadableDateTime,
  getReadableTime,
  isValidDecimal,
  isValidPaiCode,
  isValidPhoneNumber,
  isValidRange,
  isValidTaxCode,
  parseRangeValues,
} from '@/forms/utils';
import { doesNotContainUnicodeChars, findUnicodeChars } from '@/forms/unicodeCharsRule';

type RequiredValidationRule = 'required';
type MinValidationRule = `min:${number}`;
type MaxValidationRule = `max:${number}`;
type LengthValidationRule = `length:${number}`;
type BetweenValidationRule = `between:${number},${number}`;
type RegexValidationRule = `regex:${string}`;
type EmailValidationRule = 'email';
type MinValueValidationRule = `minValue:${number}`;
type MaxValueValidationRule = `maxValue:${number}`;
type NumberValidationRule = 'number';
type TaxCodeValidationRule = 'taxCode';
type MinTimeValidationRule = `minTime:${number}`;
type MaxTimeValidationRule = `maxTime:${number}`;
type MinDateTimeValidationRule = `minDateTime:${number}`;
type MaxDateTimeValidationRule = `maxDateTime:${number}`;
type MinDateValidationRule = `minDate:${number}`;
type MaxDateValidationRule = `maxDate:${number}`;
type ExtValidationRule = `ext:${string}`;
type PhoneNumberValidationRule = 'phoneNumber';
type UnicodeValidationRule = `unicode:${string}`;
type PaiCodeValidationRule = 'paiCode';
type DecimalValidationRule = 'decimal';
type RangeValueValidationRule = `rangeValue:[${number}][${number}]`;

type ValidationRule =
  | ''
  | DecimalValidationRule
  | RequiredValidationRule
  | MinValidationRule
  | MaxValidationRule
  | LengthValidationRule
  | BetweenValidationRule
  | RegexValidationRule
  | EmailValidationRule
  | MinValueValidationRule
  | MaxValueValidationRule
  | NumberValidationRule
  | TaxCodeValidationRule
  | MinTimeValidationRule
  | MaxTimeValidationRule
  | MinDateTimeValidationRule
  | MaxDateTimeValidationRule
  | MinDateValidationRule
  | MaxDateValidationRule
  | ExtValidationRule
  | PhoneNumberValidationRule
  | UnicodeValidationRule
  | PaiCodeValidationRule
  | RangeValueValidationRule;

export type InputValidation = ValidationRule | `${ValidationRule}|${ValidationRule}` | `${ValidationRule}|${ValidationRule}|${string}`;

export const defineValidationRules = () => {
  defineRule('required', required);
  defineRule('email', email);
  defineRule('min', min);
  defineRule('max', max);
  defineRule('length', length);
  defineRule('minValue', min_value);
  defineRule('maxValue', max_value);
  defineRule('number', numeric);
  defineRule('regex', regex);
  defineRule('taxCode', isValidTaxCode);
  defineRule('minTime', min_value);
  defineRule('maxTime', max_value);
  defineRule('minDateTime', min_value);
  defineRule('maxDateTime', max_value);
  defineRule('minDate', min_value);
  defineRule('maxDate', max_value);
  defineRule('ext', ext);
  defineRule('phoneNumber', isValidPhoneNumber);
  defineRule('between', between);
  defineRule('unicode', doesNotContainUnicodeChars);
  defineRule('paiCode', isValidPaiCode);
  defineRule('decimal', isValidDecimal);
  defineRule('rangeValue', isValidRange);

  configure({
    generateMessage: context => {
      if (!context.rule) return '';
      const params = context.rule.params as string[];
      switch (context.rule.name) {
        case 'required':
          return 'Campo obbligatorio';
        case 'email':
          return 'Email non valida';
        case 'min':
          return `Deve avere almeno ${params[0]} caratteri`;
        case 'max':
          return `Deve avere massimo ${params[0]} caratteri`;
        case 'length':
          return `Deve avere ${params[0]} caratteri`;
        case 'minValue':
          return `Minimo ${parseFloat(params[0])}`;
        case 'maxValue':
          return `Massimo ${parseFloat(params[0])}`;
        case 'minTime':
          return `Deve essere dopo le ${getReadableTime(parseInt(params[0]))}`;
        case 'maxTime':
          return `Deve essere entro le ${getReadableTime(parseInt(params[0]))}`;
        case 'minDateTime':
          return `Deve essere dopo il ${getReadableDateTime(parseInt(params[0]))}`;
        case 'maxDateTime':
          return `Deve essere entro il ${getReadableDateTime(parseInt(params[0]))}`;
        case 'minDate':
          return `Deve essere dopo il ${getReadableDate(parseInt(params[0]))}`;
        case 'maxDate':
          return `Deve essere entro il ${getReadableDate(parseInt(params[0]))}`;
        case 'between':
          return `Deve essere compresa tra ${params[0]} e ${params[1]}`;
        case 'regex':
          return 'Formato non valido';
        case 'decimal':
          return 'Formato non valido';
        case 'number':
          return 'Deve essere un numero';
        case 'taxCode':
          return 'Codice fiscale non valido';
        case 'ext':
          return 'Formato file non valido';
        case 'unicode':
          return `"${findUnicodeChars(params.join(','))}" caratteri non supportati`;
        case 'paiCode':
          return 'Codice contatore PAI non valido';
        case 'rangeValue': {
          const rangeStr = params[0] as string;
          const rangeValues = parseRangeValues(rangeStr);
          if (!rangeValues) return 'Campo invalido';
          return `Inserire un valore tra ${rangeValues.min} e ${rangeValues.max}`;
        }
        default:
          return 'Campo invalido';
      }
    },
  });
};
